import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
  Inject,
  OnDestroy,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { fabric } from 'fabric';

declare const MathQuill: any;

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { WbDialogService } from 'src/app/services/wb-dialog.service';
import { UiService } from 'src/app/services/ui.service';

@UntilDestroy()
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-myscript',
  templateUrl: './myscript.component.html',
  styleUrls: ['./myscript.component.scss'],
})
export class MyscriptComponent implements AfterViewInit, OnDestroy {
  @ViewChild('katexRenderer', { read: ElementRef, static: true }) katexRenderer!: ElementRef;
  @ViewChild('katextcontainer', { read: ElementRef, static: true }) katextContainer!: ElementRef;
  @ViewChild('magicCanvas', { read: ElementRef, static: false }) magicCanvasRef!: ElementRef;

  katex: string = '';
  Modes = {
    Write: 'Write',
    Paste: 'Paste',
    Magic: 'Magic',
  };
  mode = this.Modes.Write;
  mathField: any;
  snackBarOpened = false;
  isKatexError = false;
  isMobile = false;

  // Magic mode properties
  fabricCanvas?: fabric.Canvas;
  conversionTimer: any;
  isConverting = false;
  currentCanvasState: string = '';
  isModalClosing = false;

  // Cloud function URL - update this with your deployed function URL
  private imageToLatexUrl = environment.imageToLatexServer; // Change to your deployed URL

  constructor(
    public dialogRef: MatDialogRef<MyscriptComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _snackBar: MatSnackBar,
    private translateService: TranslateService,
    private sharedDataService: SessionSharedDataService,
    wbDialogService: WbDialogService,
    private uiService: UiService,
    private http: HttpClient,
  ) {
    this.uiService.isMobile.pipe(untilDestroyed(this)).subscribe((res) => {
      this.isMobile = res;
      if (this.isMobile) {
        this.mode = this.Modes.Paste;
      }
    });
    this.katex = data.katex || '';
    this.sharedDataService.isDisableKeyEvents = true;
    wbDialogService.pushNewDialogRef(dialogRef);
  }

  doneClick() {
    this.checkKatexError();
    if (this.isKatexError) {
      return;
    }
    this.isModalClosing = true;
    this.dialogRef.close(this.katex.replace(/(\r\n|\n|\r)/gm, ''));
  }

  cancelClick() {
    this.isModalClosing = true;
    this.dialogRef.close();
  }

  ngAfterViewInit(): void {
    if (!this.isMobile) {
      const MQ = MathQuill.getInterface(2);
      /*
        This is a hacky way to disable all auto conversions.
        Because empty string causes an issue in library itself, we do this way.
      */
      MQ.config({
        autoOperatorNames: 'xxxxxxxxxxxxxx',
        autoCommands: 'xxxxxxxxxxxxxx',
      });
      this.mathField = MQ.MathField(document.getElementById('math-field'), {
        spaceBehavesLikeTab: true,
        handlers: {
          edit: () => {
            this.katex = this.mathField.latex() || this.mathField.html();
          },
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.sharedDataService.isDisableKeyEvents = false;
    if (this.conversionTimer) {
      clearInterval(this.conversionTimer);
    }
    if (this.fabricCanvas) {
      this.fabricCanvas.dispose();
    }
  }

  checkKatexError() {
    this.isKatexError = false;
    if (this.katexRenderer) {
      const allChildren = this.katexRenderer.nativeElement.firstChild.getElementsByTagName('*');
      for (let i = 0; i < allChildren.length; i++) {
        if (
          (typeof allChildren[i].className === 'string' &&
            allChildren[i].className?.includes('katex-error')) ||
          allChildren[i].style?.color === 'rgb(204, 0, 0)'
        ) {
          this.isKatexError = true;
          break;
        }
      }
      if (!this.snackBarOpened && this.isKatexError) {
        this.openSnackBar(
          this.translateService.instant(
            'Latex could not be correctly rendered. Please resolve any errors',
          ),
        );
        this.snackBarOpened = true;
      }
    }
  }

  escapeLatexDelimeter() {
    /*
      Whenever $ is typed inside the latex, we should escape it because it affects to render latex.
      Because we add backslashes whenever editor is updated, unlimited backslashes will be added.
      To prevent it, replaced multiple backslashes with only 1.
      Same for several special characters like %, &, #, etc.
    */
    if (this.katex) {
      this.katex = this.katex
        .replace(/\$/g, '\\$')
        .replace(/\\\\\$/g, '\\$')
        .replace(/%/g, '\\%')
        .replace(/\\\\%/g, '\\%')
        .replace(/#/g, '\\#')
        .replace(/\\\\#/g, '\\#');
    }
  }

  updateMathField() {
    this.escapeLatexDelimeter();
    if (this.mathField) {
      this.mathField.latex(this.katex);
    }
    this.checkKatexError();
  }

  openSnackBar(message: string) {
    const ref = this._snackBar.open(message, '✕', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'latex-error',
      duration: 5000,
    });
    ref
      .afterDismissed()
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.snackBarOpened = false;
      });
  }

  clearCanvasIfNeeded(): void {
    // Clear canvas when leaving Magic mode
    if (this.mode === this.Modes.Magic && this.fabricCanvas) {
      this.fabricCanvas.clear();
      this.fabricCanvas.backgroundColor = 'white';
      this.fabricCanvas.renderAll();
      // Set to actual empty canvas state, not empty string
      this.currentCanvasState = JSON.stringify(this.fabricCanvas.toJSON(['id']));
      this.isConverting = false;

      // Clear the conversion timer when leaving magic mode
      if (this.conversionTimer) {
        clearInterval(this.conversionTimer);
        this.conversionTimer = null;
      }
    }
  }

  activeMagicMode(): void {
    this.clearCanvasIfNeeded();
    this.mode = this.Modes.Magic;
    // Use modifiedSetTimeout to avoid triggering extra change detection cycles
    modifiedSetTimeout(() => {
      this.initializeFabricCanvas();
    }, 100);
  }

  activateWriteMode(): void {
    this.clearCanvasIfNeeded();
    this.mode = this.Modes.Write;
  }

  activatePasteMode(): void {
    this.clearCanvasIfNeeded();
    this.mode = this.Modes.Paste;
  }

  initializeFabricCanvas(): void {
    if (!this.magicCanvasRef) {
      console.error('Magic canvas reference not found');
      return;
    }

    const canvasElement = this.magicCanvasRef.nativeElement;

    // Get parent container dimensions
    const parentElement = canvasElement.parentElement;
    const containerWidth = parentElement ? parentElement.clientWidth : 600;
    const containerHeight = parentElement ? parentElement.clientHeight : 300;

    canvasElement.width = containerWidth;
    canvasElement.height = containerHeight;

    // Clean up existing canvas if it exists
    if (this.fabricCanvas) {
      this.fabricCanvas.dispose();
    }

    // Initialize Fabric.js canvas - simple setup without state management
    this.fabricCanvas = new fabric.Canvas(canvasElement, {
      isDrawingMode: true,
      width: containerWidth,
      height: containerHeight,
      backgroundColor: '#f2f2f2',
    });

    // Configure drawing brush
    this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(this.fabricCanvas);
    this.fabricCanvas.freeDrawingBrush.width = 3;
    this.fabricCanvas.freeDrawingBrush.color = 'black';
    this.fabricCanvas.freeDrawingBrush.decimate = 2;

    // Reset state tracking for new canvas
    this.currentCanvasState = JSON.stringify(this.fabricCanvas.toJSON(['id']));
    this.isConverting = false;

    // Start continuous timer to check for canvas changes
    this.startContinuousConversion();
  }

  startContinuousConversion(): void {
    // Clear any existing timer
    if (this.conversionTimer) {
      clearInterval(this.conversionTimer);
    }

    // Start continuous timer that checks every 1 seconds
    this.conversionTimer = setInterval(() => {
      this.checkAndConvert();
    }, 1000);
  }

  checkAndConvert(): void {
    if (!this.fabricCanvas || this.isConverting) {
      return;
    }

    // Get current canvas state as string
    const currentState = JSON.stringify(this.fabricCanvas.toJSON(['id']));

    // Check if canvas has changed since last conversion
    if (currentState !== this.currentCanvasState) {
      this.currentCanvasState = currentState;

      // Only convert if canvas has objects
      if (this.fabricCanvas.getObjects().length > 0) {
        this.convertCanvasToLatex();
      }
    }
  }

  async convertCanvasToLatex(): Promise<void> {
    if (!this.fabricCanvas || this.isConverting) {
      return;
    }

    // Check if canvas is empty - but don't clear katex automatically
    if (!this.fabricCanvas.getObjects().length) {
      // Don't clear katex here - let the user explicitly clear if needed
      return;
    }

    this.isConverting = true;

    // Convert canvas to base64 image without manipulating the canvas
    const dataURL = this.fabricCanvas.toDataURL({
      format: 'png',
      quality: 1.0,
    });

    // Extract base64 data (remove data:image/png;base64, prefix)
    const base64Data = dataURL.split(',')[1];

    // Prepare headers
    const headers: any = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Call the cloud function
    try {
      const response = await firstValueFrom(
        this.http
          .post<{ latex: string; error?: string }>(
            this.imageToLatexUrl,
            {
              image: base64Data,
            },
            {
              headers: headers,
            },
          )
          .pipe(untilDestroyed(this)),
      );

      if (!this.isConverting) {
        return;
      }

      // Check if response has the expected latex data
      if (response.latex) {
        this.katex = response.latex.trim();
        this.updateMathField();
      }

      this.isConverting = false;
    } catch (error) {
      // Don't show errors if modal is being closed
      if (this.isModalClosing) {
        this.isConverting = false;
        return;
      }

      // Log error to telemetry/console for debugging
      console.error('LaTeX conversion failed:', error);
      console.error('Error details:', {
        message: (error as any).message,
        status: (error as any).status,
        statusText: (error as any).statusText,
        error: (error as any).error,
      });

      // TODO: Add proper telemetry logging here
      // this.telemetryService.logError('latex-conversion-failed', error);

      // Show simple error message to user
      this.openSnackBar(
        this.translateService.instant('Failed to convert drawing to LaTeX. Please try again.'),
      );

      this.isConverting = false;
    }
  }

  // Only keep clear functionality
  canMagicClear(): boolean {
    return !!(this.fabricCanvas && this.fabricCanvas.getObjects().length > 0);
  }

  magicClear(): void {
    if (!this.fabricCanvas) {
      return;
    }

    this.fabricCanvas.clear();
    this.fabricCanvas.backgroundColor = 'white';
    this.fabricCanvas.renderAll();

    this.katex = '';
    this.updateMathField();

    // Reset conversion state to prevent race conditions
    this.isConverting = false;

    // Reset canvas state tracking
    this.currentCanvasState = JSON.stringify(this.fabricCanvas.toJSON(['id']));
  }
}
